/**
 * Wave A — RBAC dual-mode flag.
 *
 * The frontend can run in three modes during the migration window:
 *   - `legacy`  : read role from JWT/User claims; permission hooks are no-ops.
 *                 (default — production behavior is unchanged unless opted in)
 *   - `wave-a`  : read permissions from `/me/permissions`; legacy claims ignored.
 *   - `dual`    : evaluate both; prefer wave-a when available; warn on disagreement.
 *
 * Vite-first: reads `import.meta.env.VITE_RBAC_MODE`. Falls back to
 * `process.env.NEXT_PUBLIC_RBAC_MODE` so the same code works under a Next.js
 * host (matching the originally specified env name in Phase 0 docs).
 *
 * @module shared/rbac/mode
 */

export type RbacMode = 'legacy' | 'wave-a' | 'dual';

const VALID: ReadonlySet<RbacMode> = new Set<RbacMode>(['legacy', 'wave-a', 'dual']);

/**
 * Resolves the active RBAC mode for the current build/runtime.
 *
 * Default: `'legacy'` — production behavior is unchanged unless the operator
 * explicitly opts in via env.
 */
export function getRbacMode(): RbacMode {
  let raw: string | undefined;
  try {
    const viteEnv =
      ((import.meta as unknown as { env?: Record<string, string | undefined> }).env) || {};
    raw = viteEnv.VITE_RBAC_MODE as string | undefined;
  } catch {
    // ignore
  }
  if (!raw && typeof process !== 'undefined' && process.env) {
    raw = process.env.NEXT_PUBLIC_RBAC_MODE;
  }
  if (raw && VALID.has(raw as RbacMode)) return raw as RbacMode;
  return 'legacy';
}

export function isWaveAEnabled(): boolean {
  const m = getRbacMode();
  return m === 'wave-a' || m === 'dual';
}

export function isLegacyMode(): boolean {
  return getRbacMode() === 'legacy';
}

export function isDualMode(): boolean {
  return getRbacMode() === 'dual';
}
